
import * as React from "react";
import { useState } from "react";

import {
  CircleDot,
  Hexagon,
  Layers,
  FlaskConical,
  Library,
  Presentation,
  ShieldCheck,
  LifeBuoy,
  Home,
  BarChart2,
  ClipboardList,
  Settings2,
  Compass,
  Map,
  Moon,
  Sun,
  BotMessageSquare,
  Camera,
  Navigation,
  LogOut,
  User,
  KeyRound,
  Eye,
  EyeOff,
  Users2,
  UserPlus,
  ScrollText,
  ExternalLink,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { useTour } from "@/contexts/useTour";
import { useTheme } from "@/contexts/ThemeContext";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { ToolbarButton } from "@/components/ui/toolbar-button";
import type { AdminTab } from "@/pages/AdminPage";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// ── Page ID type ──────────────────────────────────────────────────────────────
export type PageId =
  | "starthere"
  | "venn"
  | "radar"
  | "r3d"
  | "hypothesis"
  | "help"
  | "references"
  | "pitchdeck"
  | "admin"
  | "explainer"
  | "instagram"
  | "user-settings";

interface AppSidebarProps {
  activePage: PageId;
  onNavigate: (page: PageId) => void;
  // Admin sidebar state — only used when activePage === "admin"
  adminTab?: AdminTab;
  setAdminTab?: (tab: AdminTab) => void;
}

// ── Main nav data ─────────────────────────────────────────────────────────────
const NAV_GROUPS: {
  label: string;
  items: { id: PageId; label: string; icon: React.ElementType }[];
}[] = [
  {
    label: "Understand",
    items: [
      { id: "starthere",  label: "Start Here",  icon: Compass     },
      { id: "hypothesis", label: "Hypothesis",  icon: FlaskConical },
    ],
  },
  {
    label: "Explore",
    items: [
      { id: "venn",      label: "Venn Diagram",    icon: CircleDot  },
      { id: "radar",     label: "Radar 2D",         icon: Hexagon    },
      { id: "r3d",       label: "Radar 3D",         icon: Layers     },
      { id: "explainer", label: "Reading the Radar",  icon: Map        },
      { id: "instagram", label: "Instagram Feed",      icon: Camera     },
    ],
  },
  {
    label: "Verify",
    items: [
      { id: "references", label: "References", icon: Library  },
      { id: "help",       label: "Help",        icon: LifeBuoy },
    ],
  },
  {
    label: "Invest",
    items: [
      { id: "pitchdeck", label: "Pitch Deck", icon: Presentation },
    ],
  },
];

// ── Admin nav data ────────────────────────────────────────────────────────────
const ADMIN_NAV_ITEMS: { id: AdminTab; label: string; icon: React.ElementType }[] = [
  { id: "forecasts", label: "Forecast Data",  icon: BarChart2     },
  { id: "sources",   label: "Source Library", icon: Library       },
  { id: "audit",     label: "Audit Log",      icon: ClipboardList },
  { id: "settings",  label: "App Settings",   icon: Settings2     },
];

const SUPER_ADMIN_NAV_ITEMS: { id: AdminTab; label: string; icon: React.ElementType }[] = [
  { id: "users",              label: "User Management",   icon: Users2      },
  { id: "invites",            label: "Invite Links",      icon: UserPlus    },
  { id: "userlog",            label: "Activity Log",      icon: ScrollText  },
  { id: "welcome_passwords",  label: "Welcome Passwords", icon: KeyRound    },
];

// ── Tour restart item ────────────────────────────────────────────────────────
function TourRestartItem() {
  const { startTour } = useTour();
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={() => startTour()}
        tooltip="Take a guided tour"
        data-tour="tour-restart"
      >
        <Navigation className="size-4" />
        <span>Guided Tour</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

// ── Sage on/off toggle ───────────────────────────────────────────────────────
function SageToggleItem() {
  const { data, isLoading } = trpc.settings.getSageEnabled.useQuery();
  const utils = trpc.useUtils();
  const setEnabled = trpc.settings.setSageEnabled.useMutation({
    onSuccess: () => utils.settings.getSageEnabled.invalidate(),
  });
  const isEnabled = data?.enabled ?? true;
  if (isLoading) return null;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={() => setEnabled.mutate({ enabled: !isEnabled })}
        tooltip={isEnabled ? "Hide Sage assistant" : "Show Sage assistant"}
        aria-label={isEnabled ? "Hide Sage assistant" : "Show Sage assistant"}
      >
        <BotMessageSquare className={`size-4 ${isEnabled ? "text-[#47825F]" : "text-muted-foreground"}`} />
        <span className={isEnabled ? "" : "text-muted-foreground"}>
          {isEnabled ? "Sage On" : "Sage Off"}
        </span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

// ── Theme toggle button ──────────────────────────────────────────────────────
function ThemeToggleItem() {
  const { isDark, setTheme } = useTheme();
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={() => setTheme(isDark ? "light" : "dark")}
        tooltip={isDark ? "Switch to light mode" : "Switch to dark mode"}
        aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      >
        {isDark
          ? <Sun className="size-4 text-amber-400" />
          : <Moon className="size-4" />}
        <span>{isDark ? "Light Mode" : "Dark Mode"}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

// ── User menu footer (logout + user info) ───────────────────────────────────
function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const changePassword = trpc.users.changePassword.useMutation({
    onSuccess: () => {
      toast.success("Password updated successfully");
      onClose();
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to update password");
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPw.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    changePassword.mutate({ currentPassword: currentPw || undefined, newPassword: newPw });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-primary" />
            Change Password
          </DialogTitle>
          <DialogDescription>
            Enter your current password and choose a new one.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="current-pw">Current Password</Label>
            <div className="relative">
              <Input
                id="current-pw"
                type={showCurrent ? "text" : "password"}
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                placeholder="Current password"
                autoComplete="current-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
                aria-label={showCurrent ? "Hide" : "Show"}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-pw">New Password</Label>
            <div className="relative">
              <Input
                id="new-pw"
                type={showNew ? "text" : "password"}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNew(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
                aria-label={showNew ? "Hide" : "Show"}
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-pw">Confirm New Password</Label>
            <Input
              id="confirm-pw"
              type="password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              placeholder="Repeat new password"
              autoComplete="new-password"
            />
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={changePassword.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={changePassword.isPending || !newPw}>
              {changePassword.isPending ? "Saving…" : "Update Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserMenuFooter() {
  const { user, logout } = useAuth();
  const [changePwOpen, setChangePwOpen] = useState(false);
  if (!user) return null;
  return (
    <>
      <ChangePasswordDialog open={changePwOpen} onClose={() => setChangePwOpen(false)} />
      <SidebarMenuItem>
        <div className="flex items-center gap-2 px-2 py-1.5 w-full">
          <div className="flex items-center justify-center size-7 rounded-full bg-primary/10 shrink-0">
            <User className="size-3.5 text-primary" />
          </div>
          <div className="flex flex-col min-w-0 flex-1">
            <span className="text-xs font-medium text-foreground truncate leading-tight">{user.name ?? 'User'}</span>
          </div>
          <button
            onClick={() => setChangePwOpen(true)}
            title="Change password"
            aria-label="Change password"
            className="flex items-center justify-center size-6 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors shrink-0"
          >
            <KeyRound className="size-3.5" />
          </button>
          <button
            onClick={() => logout()}
            title="Sign out"
            aria-label="Sign out"
            className="flex items-center justify-center size-6 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
          >
            <LogOut className="size-3.5" />
          </button>
        </div>
      </SidebarMenuItem>
    </>
  );
}

// ── Admin sidebar group (visible only to admin and super_admin) ──────────────
function AdminSidebarGroup({ activePage, onNavigate }: { activePage: PageId; onNavigate: (page: PageId) => void }) {
  const { user } = useAuth();
  if (!user || (user.role !== "admin" && user.role !== "super_admin")) return null;
  return (
    <>
      <SidebarSeparator />
      <SidebarGroup>
        <SidebarGroupLabel className="text-[10px] font-bold tracking-widest uppercase text-sidebar-foreground/40 px-2">
          Admin
        </SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {/* Admin entry point — goes to admin page */}
            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={(activePage as string) === "admin"}
                onClick={() => onNavigate("admin")}
                tooltip="Admin Panel"
                data-tour="nav-admin"
              >
                <ShieldCheck className="size-4" />
                <span>Admin Panel</span>
              </SidebarMenuButton>
            </SidebarMenuItem>

          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </>
  );
}

// ── Shared sidebar shell ──────────────────────────────────────────────────────
function SidebarShell({ children }: { children: React.ReactNode }) {
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border" data-tour="sidebar">
      <SidebarHeader className="h-[4.5rem] justify-center border-b border-sidebar-border p-2">
        <div className={`flex w-full items-center gap-2 ${isCollapsed ? "justify-center" : "justify-between"}`}>
          {!isCollapsed && (
            <div className="flex min-w-0 items-center gap-2.5 pl-1">
              <div className="flex aspect-square size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary/10">
                <img
                  src="https://d2xsxph8kpxj0f.cloudfront.net/310519663270229297/i88gywhNYafBWiKD3FZzYw/lb-logo_6a402776.png"
                  alt="Lola & Ber"
                  width={32}
                  height={32}
                  className="object-contain"
                />
              </div>
              <div className="grid min-w-0 flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">Lola &amp; Ber</span>
                <span className="truncate text-[10px] uppercase leading-snug tracking-wider text-primary">
                  What If · Data &amp; Research
                </span>
              </div>
            </div>
          )}
          <ToolbarButton
            icon={isCollapsed
              ? <PanelLeftOpen className="h-5 w-5" />
              : <PanelLeftClose className="h-5 w-5" />}
            tooltip={isCollapsed ? "Expand navigation" : "Collapse navigation"}
            onClick={toggleSidebar}
            active={isCollapsed}
            className="h-10 w-10 shrink-0 px-0"
            aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
            aria-expanded={!isCollapsed}
          />
        </div>
      </SidebarHeader>
      <SidebarContent>{children}</SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>
          <UserMenuFooter />
        </SidebarMenu>
        <a
          href="https://lolandberretreats.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 py-2 opacity-50 hover:opacity-80 transition-opacity"
        >
          <ExternalLink className="size-3.5 text-muted-foreground" aria-hidden="true" />
          <span className="text-[10px] tracking-wide text-muted-foreground group-data-[collapsible=icon]:hidden">
            Visit Lola &amp; Ber
          </span>
        </a>
      </SidebarFooter>
    </Sidebar>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
export function AppSidebar({
  activePage,
  onNavigate,
  adminTab = "forecasts",
  setAdminTab,
}: AppSidebarProps) {
  const { user } = useAuth();
  // ── Settings (admin) sidebar ─────────────────────────────────────────────
  if (activePage === "admin") {
    return (
      <SidebarShell>
        {/* Home — returns to main app */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-[10px] font-bold tracking-widest uppercase text-sidebar-foreground/40 px-2">
            Navigation
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => onNavigate("starthere")}
                  tooltip="Back to App"
                >
                  <Home className="size-4" />
                  <span>Home</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarSeparator />

        {/* Admin sections */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-[10px] font-bold tracking-widest uppercase text-sidebar-foreground/40 px-2">
            Admin
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {ADMIN_NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      isActive={adminTab === item.id}
                      onClick={() => setAdminTab?.(item.id)}
                      tooltip={item.label}
                    >
                      <Icon className="size-4" />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Super-admin nav items — no label shown */}
        {user?.role === "super_admin" && (
          <>
            <SidebarSeparator />
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {SUPER_ADMIN_NAV_ITEMS.map((item) => {
                    const Icon = item.icon;
                    return (
                      <SidebarMenuItem key={item.id}>
                        <SidebarMenuButton
                          isActive={adminTab === item.id}
                          onClick={() => setAdminTab?.(item.id)}
                          tooltip={item.label}
                        >
                          <Icon className="size-4" />
                          <span>{item.label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </>
        )}
        {/* Theme toggle — pinned to bottom of admin sidebar */}
        <SidebarGroup className="mt-auto">
          <SidebarSeparator />
          <SidebarGroupContent>
            <SidebarMenu>
              <ThemeToggleItem />
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarShell>
    );
  }

  // ── Main sidebar ───────────────────────────────────────────────────────────
  return (
    <SidebarShell>
      {/* Main nav groups */}
      {NAV_GROUPS.map((group) => (
        <SidebarGroup key={group.label}>
          <SidebarGroupLabel className="text-[10px] font-bold tracking-widest uppercase text-sidebar-foreground/40 px-2">
            {group.label}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {group.items.map((item) => {
                const Icon = item.icon;
                const tourKey = item.id === "starthere" ? "nav-starthere"
                  : item.id === "hypothesis" ? "nav-hypothesis"
                  : item.id === "venn" ? "nav-venn"
                  : item.id === "radar" ? "nav-radar"
                  : item.id === "r3d" ? "nav-r3d"
                  : item.id === "explainer" ? "nav-explainer"
                  : item.id === "instagram" ? "nav-instagram"
                  : item.id === "references" ? "nav-references"
                  : item.id === "help" ? "nav-help"
                  : item.id === "pitchdeck" ? "nav-pitchdeck"
                  : undefined;
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      isActive={activePage === item.id}
                      onClick={() => onNavigate(item.id)}
                      tooltip={item.label}
                      data-tour={tourKey}
                    >
                      <Icon className="size-4" />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ))}

      {/* Admin section — visible only to admin and super_admin */}
      <AdminSidebarGroup activePage={activePage} onNavigate={onNavigate} />
      {/* Support — pinned to bottom */}
      <SidebarGroup>
        <SidebarSeparator />
        <SidebarGroupLabel className="text-[10px] font-bold tracking-widest uppercase text-sidebar-foreground/40 px-2">
          Support
        </SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                isActive={activePage === "user-settings"}
                onClick={() => onNavigate("user-settings")}
                tooltip="Settings"
              >
                <Settings2 className="size-4" />
                <span>Settings</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <TourRestartItem />
            <SageToggleItem />
            <ThemeToggleItem />
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarShell>
  );
}
